import {
  IVesselParticularsBayCounts,
  IVesselParticularsData,
  IVesselParticularsSection,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  IVesselParticularsColors,
  IVesselParticularsUnits,
  VESSEL_PARTICULARS_PRINT_COLORS,
} from "./vesselParticularsTypes";
import {
  calculateVesselParticularsSectionWidth,
  drawVesselParticularsBaySection,
} from "./drawVesselParticularsBaySection";

import { createSvgText } from "../helpers/createSvgText";

type ISectionRow = IVesselParticularsSection[];

const SVG_NS = "http://www.w3.org/2000/svg";
const FONT_FAMILY = `Helvetica, Arial, sans-serif`;
const MONO_FONT_FAMILY = `"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace`;

/** A3 landscape at 4 px per mm */
const SHEET_WIDTH = 1680;
const SHEET_HEIGHT = 1188;
const MARGIN = 42;
const INNER_WIDTH = SHEET_WIDTH - MARGIN * 2;

const HEADER_HEIGHT = 64;
const SECTION_GAP = 20;
const SECTION_TARGET_HEIGHT = 300;
const COUNTS_ROW_HEIGHT = 11;
const COUNTS_LABEL_COL = 52;

export interface IVesselParticularsLabels {
  title: string;
  onDeck: string;
  inHold: string;
  bay: string;
  lcg: string;
  teu: string;
  loa: string;
  lpp: string;
  imo: string;
  callSign: string;
  total: string;
  generatedBy?: string;
}

/**
 * Assembles the container vessel particulars as one or more sheet `<svg>`s (A3
 * landscape proportions, 4 svg units per mm). Sheet 1 holds the title, the
 * particulars, the side view with the per-bay count tables, and as many bay
 * cross-sections as fit; further sheets hold the remaining sections.
 */
export function composeVesselParticularsSheets({
  planData,
  sideViewSvg,
  units,
  labels,
  colors = VESSEL_PARTICULARS_PRINT_COLORS,
}: {
  planData: IVesselParticularsData;
  /** An already rendered side view `<svg>` (it is moved into the sheet) */
  sideViewSvg?: SVGSVGElement;
  units: IVesselParticularsUnits;
  labels: IVesselParticularsLabels;
  colors?: IVesselParticularsColors;
}): SVGSVGElement[] {
  const sheets: SVGSVGElement[] = [];

  let sheet = createSheet(colors);
  sheets.push(sheet);

  drawHeader(sheet, planData, units, labels, colors);

  let cursorY = MARGIN + HEADER_HEIGHT;

  // Side view flanked by the on-deck table above and the in-hold table below
  const bandWidth = Math.round(INNER_WIDTH * 0.62);
  cursorY += drawCountsTable(
    sheet,
    planData.bayCounts,
    "above",
    MARGIN,
    cursorY,
    bandWidth,
    units,
    labels,
    colors,
  );
  cursorY += 6;
  cursorY += placeSideView(sheet, sideViewSvg, MARGIN, cursorY, bandWidth);
  cursorY += 6;
  cursorY += drawCountsTable(
    sheet,
    planData.bayCounts,
    "below",
    MARGIN,
    cursorY,
    bandWidth,
    units,
    labels,
    colors,
  );
  cursorY += SECTION_GAP;

  // Shared mm scale so every cross-section of the plan is comparable
  const maxSectionHeightMm = Math.max(1, ...planData.sections.map((s) => s.maxVcgMm - s.minVcgMm));
  const pxPerMm = (SECTION_TARGET_HEIGHT - 60) / maxSectionHeightMm;

  // Group the sections into sheet rows by width first: every section of a row
  // is then drawn from the same top VCG, so equal tiers coincide horizontally
  // across the whole row.
  const sectionRows: ISectionRow[] = [];
  let currentRow: ISectionRow = [];
  let currentRowWidth = 0;

  planData.sections.forEach((section) => {
    const width = calculateVesselParticularsSectionWidth(section, pxPerMm);
    if (currentRow.length > 0 && MARGIN + currentRowWidth + width > SHEET_WIDTH - MARGIN) {
      sectionRows.push(currentRow);
      currentRow = [];
      currentRowWidth = 0;
    }
    currentRow.push(section);
    currentRowWidth += width + SECTION_GAP;
  });
  if (currentRow.length > 0) sectionRows.push(currentRow);

  sectionRows.forEach((row) => {
    const topVcgMm = Math.max(...row.map((s) => s.maxVcgMm));

    const drawnRow = row.map((section) =>
      drawVesselParticularsBaySection({ section, pxPerMm, colors, units, topVcgMm }),
    );

    // Sections with a shorter header get pushed down so the cells still align
    const rowContentTop = Math.max(...drawnRow.map((d) => d.contentTop));
    const rowHeight = Math.max(...drawnRow.map((d) => d.height + rowContentTop - d.contentTop));

    if (cursorY + rowHeight > SHEET_HEIGHT - MARGIN) {
      sheet = createSheet(colors);
      sheets.push(sheet);
      cursorY = MARGIN;
    }

    let cursorX = MARGIN;
    drawnRow.forEach((drawn) => {
      const yOffset = rowContentTop - drawn.contentTop;
      drawn.svgGroup.setAttribute("transform", `translate(${cursorX}, ${cursorY + yOffset})`);
      sheet.appendChild(drawn.svgGroup);
      cursorX += drawn.width + SECTION_GAP;
    });

    cursorY += rowHeight + SECTION_GAP;
  });

  sheets.forEach((s, idx) => drawFooter(s, idx + 1, sheets.length, labels, colors));

  return sheets;
}

function createSheet(colors: IVesselParticularsColors): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("viewBox", `0 0 ${SHEET_WIDTH} ${SHEET_HEIGHT}`);
  svg.setAttribute("width", `${SHEET_WIDTH}`);
  svg.setAttribute("height", `${SHEET_HEIGHT}`);
  svg.setAttribute("font-family", FONT_FAMILY);
  svg.setAttribute("class", "vessel-particulars-sheet");

  const bg = document.createElementNS(SVG_NS, "rect");
  bg.setAttribute("width", `${SHEET_WIDTH}`);
  bg.setAttribute("height", `${SHEET_HEIGHT}`);
  bg.setAttribute("fill", "#ffffff");
  svg.appendChild(bg);

  const frame = document.createElementNS(SVG_NS, "rect");
  frame.setAttribute("x", `${MARGIN / 2}`);
  frame.setAttribute("y", `${MARGIN / 2}`);
  frame.setAttribute("width", `${SHEET_WIDTH - MARGIN}`);
  frame.setAttribute("height", `${SHEET_HEIGHT - MARGIN}`);
  frame.setAttribute("fill", "none");
  frame.setAttribute("stroke", colors.tableStroke);
  frame.setAttribute("stroke-width", "1");
  svg.appendChild(frame);

  return svg;
}

function drawHeader(
  sheet: SVGSVGElement,
  planData: IVesselParticularsData,
  units: IVesselParticularsUnits,
  labels: IVesselParticularsLabels,
  colors: IVesselParticularsColors,
) {
  const p = planData.particulars;

  sheet.appendChild(
    createSvgText({
      text: p.shipName ?? "-",
      x: MARGIN,
      y: MARGIN + 20,
      fontSize: 22,
      textColor: colors.textColor,
      textAnchor: "start",
      className: "vessel-particulars-ship-name",
    }),
  );

  sheet.appendChild(
    createSvgText({
      text: labels.title.toUpperCase(),
      x: MARGIN,
      y: MARGIN + 40,
      fontSize: 12,
      textColor: colors.accentColor,
      textAnchor: "start",
    }),
  );

  // Particulars box, top right
  const fmtLen = (mm?: number) =>
    mm !== undefined ? units.lengthUnits.fromValueToFormattedDisplay(mm) : "-";
  const rows: Array<[string, string]> = [
    [labels.loa, fmtLen(p.loaMm)],
    [labels.lpp, fmtLen(p.lppMm)],
    [labels.imo, p.imoCode ?? "-"],
    [labels.callSign, p.callSign ?? "-"],
    [`${labels.teu} ${labels.onDeck}`, `${p.teuOnDeck}`],
    [`${labels.teu} ${labels.inHold}`, `${p.teuInHold}`],
    [`${labels.teu} ${labels.total}`, `${p.teuTotal}`],
  ];

  const boxWidth = 260;
  const rowH = 12;
  const boxX = SHEET_WIDTH - MARGIN - boxWidth;
  const boxY = MARGIN;

  const frame = document.createElementNS(SVG_NS, "rect");
  frame.setAttribute("x", `${boxX}`);
  frame.setAttribute("y", `${boxY}`);
  frame.setAttribute("width", `${boxWidth}`);
  frame.setAttribute("height", `${rows.length * rowH + 6}`);
  frame.setAttribute("fill", "none");
  frame.setAttribute("stroke", colors.tableStroke);
  frame.setAttribute("stroke-width", "0.8");
  sheet.appendChild(frame);

  rows.forEach(([label, value], idx) => {
    const y = boxY + (idx + 1) * rowH;
    sheet.appendChild(
      createSvgText({
        text: label,
        x: boxX + 8,
        y,
        fontSize: 7,
        textColor: colors.textColor,
        textAnchor: "start",
      }),
    );
    sheet.appendChild(
      createSvgText({
        text: value,
        x: boxX + boxWidth - 8,
        y,
        fontSize: 7,
        textColor: colors.accentColor,
        textAnchor: "end",
        fontFamily: MONO_FONT_FAMILY,
      }),
    );
  });
}

/**
 * The per-bay counts table of one level: bay numbers (fwd on the right, matching
 * the side view's bow), the 20' and 40' position counts and the 20' LCG.
 */
function drawCountsTable(
  sheet: SVGSVGElement,
  bayCounts: IVesselParticularsBayCounts[],
  level: "above" | "below",
  x: number,
  y: number,
  width: number,
  units: IVesselParticularsUnits,
  labels: IVesselParticularsLabels,
  colors: IVesselParticularsColors,
): number {
  if (bayCounts.length === 0) return 0;

  // Ascending ISO bay = fwd first; drawn right-to-left so fwd bays sit at the bow (right)
  const baysDesc = [...bayCounts].reverse();

  const isAbove = level === "above";
  const tableRows: Array<{ label: string; getValue: (b: IVesselParticularsBayCounts) => string }> =
    [
      { label: labels.bay, getValue: (b) => String(Number(b.isoBay)).padStart(2, "0") },
      {
        label: "20'",
        getValue: (b) => `${isAbove ? b.count20Above : b.count20Below}`,
      },
      {
        label: "40'",
        getValue: (b) => {
          const count = isAbove ? b.count40Above : b.count40Below;
          return count !== undefined ? `${count}` : "";
        },
      },
      {
        label: labels.lcg,
        getValue: (b) => {
          const lcg = isAbove ? b.lcg20AboveMm : b.lcg20BelowMm;
          return lcg !== undefined
            ? units.lengthUnits.fromValueToFormattedDisplay(
                units.lcgTransposer.fromValueToDisplay(lcg),
              )
            : "";
        },
      },
    ];

  const height = tableRows.length * COUNTS_ROW_HEIGHT + COUNTS_ROW_HEIGHT;
  const colWidth = (width - COUNTS_LABEL_COL) / baysDesc.length;

  sheet.appendChild(
    createSvgText({
      text: (isAbove ? labels.onDeck : labels.inHold).toUpperCase(),
      x,
      y: y + COUNTS_ROW_HEIGHT - 3,
      fontSize: 7,
      textColor: colors.textColor,
      textAnchor: "start",
    }),
  );

  const tableY = y + COUNTS_ROW_HEIGHT;
  const tableHeight = tableRows.length * COUNTS_ROW_HEIGHT;

  const frame = document.createElementNS(SVG_NS, "rect");
  frame.setAttribute("x", `${x}`);
  frame.setAttribute("y", `${tableY}`);
  frame.setAttribute("width", `${width}`);
  frame.setAttribute("height", `${tableHeight}`);
  frame.setAttribute("fill", "none");
  frame.setAttribute("stroke", colors.tableStroke);
  frame.setAttribute("stroke-width", "0.8");
  sheet.appendChild(frame);

  tableRows.forEach((row, rowIdx) => {
    const rowY = tableY + rowIdx * COUNTS_ROW_HEIGHT;

    if (rowIdx > 0) {
      sheet.appendChild(hLine(x, rowY, x + width, colors.tableStroke));
    }

    sheet.appendChild(
      createSvgText({
        text: row.label,
        x: x + 4,
        y: rowY + COUNTS_ROW_HEIGHT - 3,
        fontSize: 6,
        textColor: colors.textColor,
        textAnchor: "start",
      }),
    );

    baysDesc.forEach((bay, colIdx) => {
      const colX = x + COUNTS_LABEL_COL + colIdx * colWidth;
      if (rowIdx === 0) {
        sheet.appendChild(vLine(colX, tableY, tableY + tableHeight, colors.tableStroke));
      }
      sheet.appendChild(
        createSvgText({
          text: row.getValue(bay),
          x: colX + colWidth / 2,
          y: rowY + COUNTS_ROW_HEIGHT - 3,
          fontSize: 6,
          textColor: rowIdx === 0 ? colors.textColor : colors.accentColor,
          fontFamily: rowIdx === 0 ? undefined : MONO_FONT_FAMILY,
          letterSpacing: rowIdx === 0 ? undefined : "-0.05em",
        }),
      );
    });
  });

  return height;
}

/** Embeds the (already rendered) side view scaled to the band width. Returns the used height */
function placeSideView(
  sheet: SVGSVGElement,
  sideViewSvg: SVGSVGElement | undefined,
  x: number,
  y: number,
  width: number,
): number {
  if (!sideViewSvg) return 0;

  const intrinsicWidth =
    Number(sideViewSvg.getAttribute("width")?.replace("px", "")) ||
    sideViewSvg.viewBox?.baseVal?.width ||
    0;
  const intrinsicHeight =
    Number(sideViewSvg.getAttribute("height")?.replace("px", "")) ||
    sideViewSvg.viewBox?.baseVal?.height ||
    0;

  if (!intrinsicWidth || !intrinsicHeight) return 0;

  const height = Math.round((intrinsicHeight / intrinsicWidth) * width);

  sideViewSvg.setAttribute("x", `${x}`);
  sideViewSvg.setAttribute("y", `${y}`);
  sideViewSvg.setAttribute("width", `${width}`);
  sideViewSvg.setAttribute("height", `${height}`);
  if (!sideViewSvg.getAttribute("viewBox")) {
    sideViewSvg.setAttribute("viewBox", `0 0 ${intrinsicWidth} ${intrinsicHeight}`);
  }
  sideViewSvg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  sheet.appendChild(sideViewSvg);

  return height;
}

function drawFooter(
  sheet: SVGSVGElement,
  page: number,
  totalPages: number,
  labels: IVesselParticularsLabels,
  colors: IVesselParticularsColors,
) {
  if (labels.generatedBy) {
    sheet.appendChild(
      createSvgText({
        text: labels.generatedBy,
        x: MARGIN,
        y: SHEET_HEIGHT - MARGIN / 2 + 8,
        fontSize: 6,
        textColor: colors.hatchStroke,
        textAnchor: "start",
      }),
    );
  }

  sheet.appendChild(
    createSvgText({
      text: `${page} / ${totalPages}`,
      x: SHEET_WIDTH - MARGIN,
      y: SHEET_HEIGHT - MARGIN / 2 + 8,
      fontSize: 6,
      textColor: colors.hatchStroke,
      textAnchor: "end",
    }),
  );
}

function hLine(x1: number, y: number, x2: number, stroke: string): SVGLineElement {
  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("x1", `${x1}`);
  line.setAttribute("y1", `${y}`);
  line.setAttribute("x2", `${x2}`);
  line.setAttribute("y2", `${y}`);
  line.setAttribute("stroke", stroke);
  line.setAttribute("stroke-width", "0.4");
  return line;
}

function vLine(x: number, y1: number, y2: number, stroke: string): SVGLineElement {
  const line = document.createElementNS(SVG_NS, "line");
  line.setAttribute("x1", `${x}`);
  line.setAttribute("y1", `${y1}`);
  line.setAttribute("x2", `${x}`);
  line.setAttribute("y2", `${y2}`);
  line.setAttribute("stroke", stroke);
  line.setAttribute("stroke-width", "0.4");
  return line;
}
